import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress
} from '@mui/material';
import {
  Build as BuildIcon,
  Security as SecurityIcon,
  Clear as ClearIcon,
  DeleteSweep as DeleteSweepIcon,
  Sync as SyncIcon
} from '@mui/icons-material';
import { GlobalAdminOnly } from '../components/PermissionGate';
import ProviderManagement from '../components/ProviderManagement';
import MonitorUnitCard from '../components/monitors/MonitorUnitCard';
import api from '../services/api';
import { Provider } from '../types/providers';
import {
  filterUiSupportedProviders,
  getUiSupportedProviderTypes,
} from '../utils/uiSupportedProviders';
import { PageTransition } from '../components/PageTransition';
import { useNotification } from '../contexts/NotificationContext';
import ResearchSettingsCard from '../components/research/ResearchSettingsCard';
import ResearchModelsCard from '../components/research/ResearchModelsCard';

const Maintenance: React.FC = () => {
  const { showNotification } = useNotification();
  const [clearingTransactions, setClearingTransactions] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deletingTerminated, setDeletingTerminated] = useState(false);
  const [deleteTerminatedDialogOpen, setDeleteTerminatedDialogOpen] = useState(false);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [selectedProviderId, setSelectedProviderId] = useState<string>('');
  const [syncingRegions, setSyncingRegions] = useState(false);

  // Load providers for the region-sync selector, defaulting to the first active provider.
  useEffect(() => {
    const loadProviders = async () => {
      try {
        const data = filterUiSupportedProviders(
          await api.getProviders(),
          (p) => p.provider_type
        );
        setProviders(data);
        setSelectedProviderId((prev) => {
          if (prev && data.some((p) => p.id === prev)) return prev;
          const active = data.find((p) => p.status === 'active');
          return active?.id || data[0]?.id || '';
        });
      } catch (error) {
        console.error('Failed to load providers for region sync:', error);
      }
    };
    loadProviders();
  }, []);

  const handleSyncRegions = async () => {
    if (!selectedProviderId) {
      showNotification('Select a provider first', 'warning');
      return;
    }
    setSyncingRegions(true);
    try {
      // Discovery runs in a worker over Redis: enqueue, poll for the result, then reconcile.
      const started = await api.startProviderRegionSync(selectedProviderId);
      const maxAttempts = 60;
      let op: Awaited<ReturnType<typeof api.getProviderRegionSyncOperation>> | null = null;
      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        const current = await api.getProviderRegionSyncOperation(selectedProviderId, started.operation_id);
        if (current.status === 'completed' || current.status === 'failed') {
          op = current;
          break;
        }
      }
      if (!op) {
        showNotification('Region discovery timed out — please try again', 'error');
        return;
      }
      if (op.status === 'failed') {
        showNotification(op.error || op.message || 'Region discovery failed', 'error');
        return;
      }
      if (op.created.length === 0 && op.disabled.length === 0) {
        showNotification(op.message, 'info', 8000);
        return;
      }
      const applied = await api.applyProviderRegionSync(selectedProviderId, op.operation_id);
      showNotification(applied.message, 'success', 8000);
    } catch (error: any) {
      showNotification(
        error.response?.data?.detail || 'Failed to sync available regions',
        'error'
      );
    } finally {
      setSyncingRegions(false);
    }
  };

  const handleClearTerminatingTransactions = async () => {
    setConfirmDialogOpen(false);
    setClearingTransactions(true);
    try {
      const result = await api.clearTerminatingTransactions();
      showNotification(result.message, 'success');
    } catch (error: any) {
      showNotification(error.response?.data?.detail || 'Failed to clear terminating transactions', 'error');
    } finally {
      setClearingTransactions(false);
    }
  };

  const handleDeleteTerminatedBots = async () => {
    setDeleteTerminatedDialogOpen(false);
    setDeletingTerminated(true);
    try {
      const result = await api.deleteTerminatedBots(10);
      showNotification(
        `Deleted ${result.deleted_count} safely terminated bot record${result.deleted_count !== 1 ? 's' : ''}`,
        'success'
      );
    } catch (error: any) {
      showNotification(error.response?.data?.detail || 'Failed to delete terminated bots', 'error');
    } finally {
      setDeletingTerminated(false);
    }
  };


  const supportedProviderLabel = getUiSupportedProviderTypes()
    .map((t) => t.toUpperCase())
    .join(', ');

  const maintenanceTasks = [
    {
      id: 'providers',
      title: 'Provider Management',
      description: supportedProviderLabel
        ? `Manage cloud providers (${supportedProviderLabel})`
        : 'Manage cloud providers',
      icon: <SecurityIcon />,
      status: 'ready',
      action: () => {}, // No action needed, handled by component
      details: 'Configure and manage cloud provider settings'
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'success';
      case 'warning': return 'warning';
      case 'error': return 'error';
      default: return 'default';
    }
  };

  return (
    <GlobalAdminOnly fallback={
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h4" color="text.secondary" gutterBottom>
          Access Denied
        </Typography>
        <Typography variant="body1" color="text.secondary">
          You need global admin permissions to access the Maintenance page.
        </Typography>
      </Box>
    }>
      <PageTransition>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 4 }}>
            System Maintenance
          </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card><CardHeader title="Fleet Manager" subheader="Container host capacity, bot distribution and SSH access" />
            <CardContent><Button component={Link} to="/maintenance/fleet" variant="outlined">Open Fleet Manager</Button></CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}><ResearchModelsCard /></Grid>
          <Grid item xs={12}><ResearchSettingsCard /></Grid>
        {/* Maintenance Tasks */}
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="Maintenance Tasks"
              subheader="Perform system maintenance and synchronization tasks"
              avatar={<BuildIcon color="primary" />}
            />
            <CardContent>
              <Grid container spacing={2}>
                {maintenanceTasks.map((task) => (
                  <Grid item xs={12} key={task.id}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Box sx={{ mr: 2, color: 'primary.main' }}>
                            {task.icon}
                          </Box>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="h6" gutterBottom>
                              {task.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {task.description}
                            </Typography>
                          </Box>
                          <Chip
                            label={task.status}
                            color={getStatusColor(task.status) as any}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                        
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {task.details}
                        </Typography>
                        
                        <Box sx={{ mt: 2 }}>
                          <ProviderManagement />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Bot Maintenance Actions */}
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="Bot Maintenance"
              subheader="Maintenance actions for bot management"
              avatar={<BuildIcon color="primary" />}
            />
            <CardContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Clear Terminating Bot Transactions
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Sets transaction_id to null for all bots with status 'terminating'. This can help release stuck bots that are in a terminating state.
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<ClearIcon />}
                    onClick={() => setConfirmDialogOpen(true)}
                    disabled={clearingTransactions}
                  >
                    {clearingTransactions ? 'Clearing...' : 'Clear Terminating Transactions'}
                  </Button>
                </Box>

                <Box>
                  <Typography variant="h6" gutterBottom>
                    Delete Terminated Bots
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Permanently removes bot records with status "terminated" that were last updated more than
                    10 minutes ago. Only bots whose status is definitively terminated are eligible — no bots
                    in any other state are touched.
                  </Typography>
                  <Button
                    variant="contained"
                    color="warning"
                    startIcon={<DeleteSweepIcon />}
                    onClick={() => setDeleteTerminatedDialogOpen(true)}
                    disabled={deletingTerminated}
                  >
                    {deletingTerminated ? 'Deleting...' : 'Delete Terminated Bots'}
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Region Synchronization */}
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="Region Synchronization"
              subheader="Discover and sync available cloud regions for a provider"
              avatar={<BuildIcon color="primary" />}
            />
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Sync Available Regions
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Queries the selected provider for the cloud regions available to its
                account and reconciles the region list: any newly available region is
                added (disabled by default), and any configured region no longer offered
                by the cloud is disabled. Existing regions are never deleted.
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <FormControl
                  size="small"
                  sx={{ minWidth: 260 }}
                  disabled={syncingRegions || providers.length === 0}
                >
                  <InputLabel id="region-sync-provider-label">Provider</InputLabel>
                  <Select
                    labelId="region-sync-provider-label"
                    label="Provider"
                    value={selectedProviderId}
                    onChange={(e) => setSelectedProviderId(e.target.value)}
                  >
                    {providers.map((provider) => (
                      <MenuItem key={provider.id} value={provider.id}>
                        {provider.name} ({provider.provider_type.toUpperCase()})
                        {provider.status !== 'active' ? ' — inactive' : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={
                    syncingRegions
                      ? <CircularProgress size={18} color="inherit" />
                      : <SyncIcon />
                  }
                  onClick={handleSyncRegions}
                  disabled={syncingRegions || !selectedProviderId}
                >
                  {syncingRegions ? 'Syncing...' : 'Sync Available Regions'}
                </Button>
              </Box>
              {providers.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  No providers available.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Monitor Units */}
        <Grid item xs={12}>
          <MonitorUnitCard />
        </Grid>

      </Grid>

      {/* Confirmation Dialog - Clear Terminating Transactions */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
      >
        <DialogTitle id="confirm-dialog-title">
          Clear Terminating Bot Transactions
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description">
            Are you sure you want to clear the transaction_id for all bots with status 'terminating'? 
            This action will release transaction locks on all terminating bots and cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleClearTerminatingTransactions} 
            color="primary" 
            variant="contained"
            autoFocus
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog - Delete Terminated Bots */}
      <Dialog
        open={deleteTerminatedDialogOpen}
        onClose={() => setDeleteTerminatedDialogOpen(false)}
      >
        <DialogTitle>
          Delete terminated bots from database?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This permanently removes bot records whose status is "terminated" and that were last
            updated more than 10 minutes ago, ensuring all in-flight processing is complete.
          </DialogContentText>
          <DialogContentText sx={{ mt: 1 }}>
            Bots in any other status (running, stopping, error, etc.) are never touched.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTerminatedDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteTerminatedBots}
            color="warning"
            variant="contained"
            autoFocus
          >
            Delete Terminated Bots
          </Button>
        </DialogActions>
      </Dialog>
        </Box>
      </PageTransition>
    </GlobalAdminOnly>
  );
};

export default Maintenance;
